/**
 * Choosing your status, and deciding what it does.
 *
 * A status that is only a coloured dot is decoration. These carry behaviour -
 * Do not disturb can silence the app, Idle can set itself, Invisible can hide
 * what you are playing - and each of those is a setting rather than a rule,
 * because everybody draws the line somewhere different.
 *
 * Opened from your own picture in the top bar, which is where people look for
 * it, and it doubles as the way into the rest of settings.
 */

import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { useSession } from '@/stores/session';
import { go } from '@/lib/hash';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';
import { Avatar } from './Avatar';
import { UserName } from './user';
import { AvailabilitySheet, timeLeft } from './Availability';
import { useToast } from './ui';

export type StatusId = 'online' | 'idle' | 'dnd' | 'invisible';

export const STATUSES: { id: StatusId; label: string; note: string; colour: string }[] = [
  { id: 'online', label: 'Online', note: 'Everything as normal', colour: 'var(--good)' },
  { id: 'idle', label: 'Away', note: 'Here, but not at the keyboard', colour: 'var(--warn)' },
  { id: 'dnd', label: 'Do not disturb', note: 'No sounds, no pop-ups', colour: 'var(--bad)' },
  { id: 'invisible', label: 'Invisible', note: 'You appear offline to everyone', colour: 'var(--text-ghost)' }
];

export function statusLook(id: string | undefined) {
  return STATUSES.find((s) => s.id === id) ?? STATUSES[0]!;
}

/** The dot, wherever a status needs drawing on its own. */
export function StatusDot({ status, size = 10 }: { status?: string; size?: number }) {
  const look = statusLook(status);
  return (
    <span
      className="inline-block flex-none rounded-full"
      style={{ width: size, height: size, background: look.colour }}
    />
  );
}

export function StatusMenu({ onClose, anchorRight }: { onClose: () => void; anchorRight?: boolean }) {
  const me = useSession((s) => s.me);
  const settings = useSession((s) => s.settings);
  const refresh = useSession((s) => s.refresh);
  const toast = useToast();
  const box = useRef<HTMLDivElement>(null);
  const [busy, setBusy] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [availOpen, setAvailOpen] = useState(false);

  useEffect(() => {
    const away = (e: PointerEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) onClose();
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('pointerdown', away);
    window.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', away);
      window.removeEventListener('keydown', esc);
    };
  }, [onClose]);

  if (!me) return null;

  async function pick(id: StatusId) {
    /*
     * Yours changes now, not when the server agrees.
     *
     * The dot used to wait for the round trip and then for a full refresh of
     * the account, so picking Do not disturb sat on Online for as long as the
     * network took - which on a slow connection reads as the menu having done
     * nothing. The server's own presence broadcast confirms it a moment later
     * and would overwrite this with the same value.
     */
    const was = me?.status ?? 'online';
    useSession.setState((st) => (st.me ? { me: { ...st.me, status: id } } : st));
    setBusy(true);
    onClose();
    try {
      await api.patch('/api/me', { status: id });
      await refresh();
    } catch (err) {
      // Put the old one back rather than leaving a dot that lies.
      useSession.setState((st) => (st.me ? { me: { ...st.me, status: was } } : st));
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const rules = settings?.statusRules;
  const free = me.available && me.available.until > Date.now() ? me.available : null;

  async function setRule(patch: Record<string, unknown>) {
    try {
      await api.patch('/api/me/settings', { statusRules: patch });
      await refresh();
    } catch (err) {
      toast((err as Error).message, 'bad');
    }
  }

  if (availOpen) return <AvailabilitySheet onClose={() => { setAvailOpen(false); onClose(); }} />;

  return (
    <div
      ref={box}
      className={cn(
        'absolute top-full z-[70] mt-2 w-[268px] overflow-hidden rounded-[calc(14px*var(--radius-mul,1))]',
        'border border-line bg-surface-1 shadow-[0_20px_50px_-10px_rgba(0,0,0,.7)]',
        anchorRight ? 'right-0' : 'left-0'
      )}
    >
      {/* Who you are, so the menu says whose status this is. */}
      <button
        type="button"
        onClick={() => { onClose(); go('u/' + me.username); }}
        className="flex w-full items-center gap-2.5 border-b border-line-soft p-3 text-left transition-colors hover:bg-surface-2"
      >
        <Avatar user={me} size={34} presence />
        <span className="min-w-0 flex-1">
          <UserName user={me} profile={false} className="block text-[13.5px] font-bold" />
          <span className="block truncate font-mono text-[11px] text-mute">@{me.username}</span>
        </span>
        <Icon name="chevronR" size={14} className="flex-none text-ghost" />
      </button>

      {!showRules ? (
        <>
          <div className="py-1">
            {STATUSES.map((s) => {
              const on = (me.status ?? 'online') === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  disabled={busy}
                  onClick={() => void pick(s.id)}
                  className={cn(
                    'flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors',
                    on ? 'bg-accent-soft' : 'hover:bg-surface-2'
                  )}
                >
                  <StatusDot status={s.id} size={11} />
                  <span className="min-w-0 flex-1">
                    <span className={cn('block text-[13px] font-semibold', on && 'text-accent-hi')}>
                      {s.label}
                    </span>
                    <span className="block truncate text-[11px] text-mute">{s.note}</span>
                  </span>
                  {on ? <Icon name="check" size={14} className="flex-none text-accent-hi" /> : null}
                </button>
              );
            })}
          </div>

          {/* Whether you want to be asked, which is a different question from
              whether the app is open. */}
          <div className="border-t border-line-soft p-1.5">
            <button
              type="button"
              onClick={() => setAvailOpen(true)}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-[calc(10px*var(--radius-mul,1))] px-2 py-2 text-left transition-colors',
                free ? 'bg-accent-soft' : 'hover:bg-surface-2'
              )}
            >
              <span className={cn('grid h-7 w-7 flex-none place-items-center rounded-[9px]', free ? 'bg-accent text-accent-fg' : 'bg-surface-3 text-mute')}>
                <Icon name="bolt" size={15} />
              </span>
              <span className="min-w-0 flex-1">
                <span className={cn('block text-[13px] font-semibold', free && 'text-accent-hi')}>Available to play</span>
                <span className="block truncate text-[11px] text-mute">
                  {free ? timeLeft(free.until) : 'Say what you are free for, and for how long'}
                </span>
              </span>
              <Icon name="chevronR" size={14} className="flex-none text-ghost" />
            </button>
          </div>

          <div className="border-t border-line-soft p-1.5">
            <MenuRow icon="sliders" label="What each status does" onClick={() => setShowRules(true)} />
            <MenuRow icon="profile" label="Edit profile" onClick={() => { onClose(); go('settings/profile'); }} />
          </div>
        </>
      ) : (
        <div className="p-3">
          <button
            type="button"
            onClick={() => setShowRules(false)}
            className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-mute transition-colors hover:text-fg"
          >
            <Icon name="chevronL" size={13} />
            Back
          </button>

          <Group colour="var(--bad)" title="Do not disturb">
            <Check
              on={rules?.dnd.sounds === false}
              label="Silence sounds"
              onChange={(v) => void setRule({ dnd: { sounds: !v } })}
            />
            <Check
              on={rules?.dnd.toasts === false}
              label="Hide pop-ups"
              onChange={(v) => void setRule({ dnd: { toasts: !v } })}
            />
            <Check
              on={!!rules?.dnd.exceptDms}
              label="Still allow direct messages through"
              onChange={(v) => void setRule({ dnd: { exceptDms: v } })}
            />
          </Group>

          <Group colour="var(--warn)" title="Away">
            <Check
              on={!!rules?.idle.auto}
              label="Set itself when I stop typing"
              onChange={(v) => void setRule({ idle: { auto: v } })}
            />
            {rules?.idle.auto ? (
              <label className="mt-1 flex items-center gap-2 pl-6 text-[12px] text-mute">
                After
                <input
                  type="number"
                  min={1}
                  max={120}
                  value={rules.idle.afterMinutes}
                  onChange={(e) => void setRule({ idle: { afterMinutes: Number(e.target.value) } })}
                  className="w-[58px] rounded-[calc(8px*var(--radius-mul,1))] border border-line-soft bg-surface-inset px-2 py-1 text-[12px] text-fg outline-none focus:border-line"
                />
                minutes
              </label>
            ) : null}
          </Group>

          <Group colour="var(--text-ghost)" title="Invisible">
            <Check
              on={!!rules?.invisible.hidePlaying}
              label="Hide what I am playing"
              onChange={(v) => void setRule({ invisible: { hidePlaying: v } })}
            />
          </Group>
        </div>
      )}
    </div>
  );
}

function MenuRow({ icon, label, onClick }: { icon: string; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2.5 rounded-[calc(10px*var(--radius-mul,1))] px-2.5 py-2 text-left text-[12.5px] font-semibold text-dim transition-colors hover:bg-surface-2 hover:text-fg"
    >
      <Icon name={icon} size={14} className="flex-none text-mute" />
      {label}
    </button>
  );
}

function Group({ colour, title, children }: {
  colour: string; title: string; children: React.ReactNode;
}) {
  return (
    <div className="mb-3 border-l-2 pl-2.5" style={{ borderColor: colour }}>
      <div className="mb-1 text-[10px] font-extrabold tracking-[.11em] text-ghost uppercase">{title}</div>
      {children}
    </div>
  );
}

function Check({ on, label, onChange }: {
  on: boolean; label: string; onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-2 py-1 text-[12.5px] text-dim">
      <input
        type="checkbox"
        checked={on}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-[3px] h-[13px] w-[13px] flex-none accent-[var(--accent)]"
      />
      <span>{label}</span>
    </label>
  );
}
